import { z } from 'zod';
import { UUIDSchema } from '../ids.js';
export const Weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
// HH:MM 24-hour, hours 00-23, minutes 00-59. `23:59` is the highest valid end.
const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
function hhmmToMinutes(s) {
    const [h, m] = s.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
export const ScheduleWindow = z
    .object({
    days: z.array(Weekday).min(1),
    start: z.string().regex(HHMM, 'start must be HH:MM 24h (00:00-23:59)'),
    end: z.string().regex(HHMM, 'end must be HH:MM 24h (00:00-23:59)'),
})
    .refine((w) => hhmmToMinutes(w.start) < hhmmToMinutes(w.end), {
    message: 'start must be strictly before end',
    path: ['end'],
});
export const HolidayOverride = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    // 'closed' = no access regardless of window; 'open' = override matching window
    mode: z.enum(['closed', 'open']),
    windows: z.array(ScheduleWindow).optional(),
});
export const Schedule = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema,
    name: z.string(),
    windows: z.array(ScheduleWindow),
    holiday_overrides: z.array(HolidayOverride),
    unifi_schedule_id: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
function noOverlap(windows) {
    // For each weekday, ensure the (start,end) intervals are disjoint.
    const byDay = new Map();
    for (const w of windows) {
        for (const day of w.days) {
            const arr = byDay.get(day) ?? [];
            arr.push([hhmmToMinutes(w.start), hhmmToMinutes(w.end)]);
            byDay.set(day, arr);
        }
    }
    for (const arr of byDay.values()) {
        arr.sort((a, b) => a[0] - b[0]);
        for (let i = 1; i < arr.length; i++) {
            // Bounded loop indices on a freshly-built array.
            /* eslint-disable security/detect-object-injection */
            const cur = arr[i];
            const prev = arr[i - 1];
            /* eslint-enable security/detect-object-injection */
            if (!cur || !prev)
                continue;
            if (cur[0] < prev[1])
                return false;
        }
    }
    return true;
}
export const CreateScheduleBody = z
    .object({
    name: z.string().min(1).max(120),
    windows: z.array(ScheduleWindow).min(1),
    holiday_overrides: z.array(HolidayOverride).default([]),
})
    .refine((b) => noOverlap(b.windows), {
    message: 'windows overlap within the same weekday',
    path: ['windows'],
});
export const UpdateScheduleBody = z
    .object({
    name: z.string().min(1).max(120).optional(),
    windows: z.array(ScheduleWindow).min(1).optional(),
    holiday_overrides: z.array(HolidayOverride).optional(),
})
    .refine((b) => !b.windows || noOverlap(b.windows), {
    message: 'windows overlap within the same weekday',
    path: ['windows'],
});
//# sourceMappingURL=schedule.js.map